/**
 * Creates a debounced event batch handler that collects frequent event
 * payloads over a short period and processes them together as a single batch.
 *
 * This is useful in scenarios where many occurrences of an event happen
 * in quick succession and it's more efficient to handle them together
 * rather than individually (e.g., view impressions, user interactions,
 * or logging).
 *
 * The handler accumulates payloads internally and either:
 * - processes them when a maximum number is reached (`maxBatch`), or
 * - after a delay of inactivity (`delay`), whichever comes first.
 *
 * @param {Function} batchProcessor - A function called with an array of
 * collected payloads once batching is triggered.
 *   Signature: (batch: Array<*>) => void
 * @param {number} [delay=500] - Time in milliseconds to wait before
 * processing the current batch (if `maxBatch` is not reached).
 * @param {number} [maxBatch=50] - Maximum number of items to collect
 * before forcing early processing.
 * @returns {Function} - A debounced event handler that accepts individual
 * payloads and batches them for efficient processing.
 *   Signature: (payload: *) => void
 */
function createDebouncedEventBatchHandler(
  batchProcessor,
  delay = 500,
  maxBatch = 50
) {
  /**
   * Internal queue to collect incoming payloads until they are processed.
   * @type {Array<*>}
   */
  let payloadQueue = [];

  /**
   * ID of the scheduled timeout to trigger processing after the delay.
   * @type {number|null}
   */
  let timeoutId = null;

  /**
   * Processes the currently buffered payloads up to the batch size limit.
   * If the queue contains more than `maxBatch` items, it processes only a chunk.
   * @private
   */
  function process() {
    let batch;
    if (payloadQueue.length <= maxBatch) {
      batch = payloadQueue;
      payloadQueue = [];
    } else {
      batch = payloadQueue.slice(0, maxBatch);
      payloadQueue = payloadQueue.slice(maxBatch);
    }
    batchProcessor(batch);
  }

  /**
   * Handler function returned to accumulate payloads and schedule processing.
   * @param {*} payload - The individual payload to be added to the batch queue.
   */
  return (payload) => {
    payloadQueue.push(payload);

    if (payloadQueue.length >= maxBatch) {
      if (timeoutId) clearTimeout(timeoutId);
      process();
      return;
    }

    if (timeoutId) clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      if (payloadQueue.length) process();
    }, delay);
  };
}

module.exports = createDebouncedEventBatchHandler;
